import { NeonColors, neonText, neonPanel, telegramStarIcon } from '../neon-theme'

interface PoolBreakdown {
  poolFreeSparks: number
  poolFreeStars: number
  inGameSparks: number
  inGameStars: number
  userBalanceSparks: number
  userBalanceStars: number
  totalSystemSparks: number
  totalSystemStars: number
  totalDeposited: number
  totalPaidOut: number
  expectedTotal: number
  conservationDelta: number
  conservationOk: boolean
  virtualInfinitePoolMode: boolean
  virtualPoolStartSparks: number
}

export class AdminPoolBreakdown {
  private container: HTMLElement | null = null
  private refreshTimer: ReturnType<typeof setInterval> | null = null
  private currentData: PoolBreakdown | null = null
  private statusMessage = ''
  private statusIsError = false

  constructor(private readonly token: string) {}

  render(parent: HTMLElement): void {
    this.container = parent
    this.container.innerHTML = '<div style="padding: 16px; color: #aaa;">Loading pool data...</div>'
    void this.fetchBreakdown()
    this.refreshTimer = setInterval(() => void this.fetchBreakdown(), 5_000)
  }

  dispose(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
      this.refreshTimer = null
    }
  }

  private async fetchBreakdown(): Promise<void> {
    try {
      const res = await fetch('/api/admin/game-pool/breakdown', {
        headers: { Authorization: `Bearer ${this.token}` },
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error)
      this.currentData = json.data as PoolBreakdown
      this.renderBreakdown(this.currentData)
    } catch (err) {
      if (this.container) {
        this.container.innerHTML = `<div style="padding: 16px; color: ${NeonColors.danger};">
          Failed to load pool: ${err instanceof Error ? err.message : 'unknown error'}
        </div>`
      }
    }
  }

  private renderBreakdown(data: PoolBreakdown): void {
    if (!this.container) return

    const isDiagnosticOnly = data.virtualInfinitePoolMode
    const depositedStars = Math.floor(data.totalDeposited / 100)
    const paidOutStars = Math.floor(data.totalPaidOut / 100)
    const expectedStars = Math.floor(data.expectedTotal / 100)
    const deltaStars = Math.floor(data.conservationDelta / 100)

    const conservationColor = isDiagnosticOnly
      ? NeonColors.gold
      : (data.conservationOk ? NeonColors.success : NeonColors.danger)
    const conservationGlow = isDiagnosticOnly
      ? NeonColors.goldGlow
      : (data.conservationOk ? NeonColors.successGlow : NeonColors.dangerGlow)
    const conservationStatus = isDiagnosticOnly
      ? `VIRTUAL POOL MODE: this screen shows DB pool accounting only; live rooms run on a shadow pool that starts at ${data.virtualPoolStartSparks} sparks`
      : (data.conservationOk
        ? 'OK: DB conservation law holds'
        : `MISMATCH: Delta ${data.conservationDelta} sparks (${deltaStars}${telegramStarIcon(14)})`)

    this.container.innerHTML = `
      <div style="padding: 16px;">
        <h2 style="
          margin: 0 0 16px 0; font-size: 1.1rem; font-weight: 700;
          ${neonText(NeonColors.gold, NeonColors.goldGlow)}
        ">Game Pool Breakdown</h2>

        <div style="display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin-bottom: 12px;">
          <button id="pool-set-10k-btn" style="
            background: ${NeonColors.info};
            color: ${NeonColors.bgDark};
            border: 1px solid ${NeonColors.info};
            border-radius: 8px;
            padding: 8px 14px;
            font-size: 0.78rem;
            font-weight: 700;
            cursor: pointer;
          ">SET DB POOL = 10K SPARKS</button>
          <span id="pool-action-status" style="
            font-size: 0.75rem;
            color: ${this.statusIsError ? NeonColors.danger : NeonColors.textDim};
            min-height: 16px;
          ">${this.escapeHtml(this.statusMessage)}</span>
        </div>

        <div style="
          ${neonPanel()}
          padding: 12px 16px; margin-bottom: 16px;
          border-color: ${conservationColor};
          display: flex; align-items: center; gap: 8px;
        ">
          <span style="font-size: 1.2rem;">${isDiagnosticOnly ? '!' : (data.conservationOk ? 'OK' : 'X')}</span>
          <span style="font-size: 0.82rem; font-weight: 600; ${neonText(conservationColor, conservationGlow)}">
            ${conservationStatus}
          </span>
        </div>

        ${isDiagnosticOnly ? `
          <div style="
            ${neonPanel()}
            margin-bottom: 16px;
            border-color: ${NeonColors.textDim};
            color: ${NeonColors.textBright};
            font-size: 0.78rem;
            line-height: 1.45;
          ">
            This tab is legacy/diagnostic in production. For gameplay balance, look at room graphs and room economy cards instead of this DB pool screen.
          </div>
        ` : ''}

        <div style="display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 20px;">
          ${this.renderCard(isDiagnosticOnly ? 'DB Pool Free' : 'Pool Free', data.poolFreeStars, data.poolFreeSparks, NeonColors.info, NeonColors.infoGlow)}
          ${this.renderCard('In Game', data.inGameStars, data.inGameSparks, NeonColors.primary, NeonColors.primaryGlow)}
          ${this.renderCard('User Balances', data.userBalanceStars, data.userBalanceSparks, NeonColors.gold, NeonColors.goldGlow)}
        </div>

        <div style="display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 20px;">
          ${this.renderCard('System Total', data.totalSystemStars, data.totalSystemSparks, NeonColors.success, NeonColors.successGlow)}
          ${this.renderCard('Expected', expectedStars, data.expectedTotal, NeonColors.textMuted, NeonColors.textDim)}
        </div>

        <div style="${neonPanel()} padding: 16px;">
          <h3 style="
            margin: 0 0 12px 0; font-size: 0.9rem; font-weight: 600;
            color: ${NeonColors.textMuted};
          ">Accounting</h3>
          <table style="width: 100%; border-collapse: collapse; font-family: monospace;">
            <tbody>
              ${this.renderRow(isDiagnosticOnly ? 'DB Deposited (lifetime)' : 'Total Deposited (lifetime)', depositedStars, data.totalDeposited, NeonColors.success)}
              ${this.renderRow(isDiagnosticOnly ? 'DB Paid Out (lifetime)' : 'Total Paid Out (lifetime)', paidOutStars, data.totalPaidOut, NeonColors.danger)}
              ${this.renderRow('Expected in System', expectedStars, data.expectedTotal, NeonColors.textMuted)}
              ${this.renderSeparator()}
              ${this.renderRow(isDiagnosticOnly ? 'DB Pool (free)' : 'Pool (free)', data.poolFreeStars, data.poolFreeSparks, NeonColors.info)}
              ${this.renderRow('In Active Games', data.inGameStars, data.inGameSparks, NeonColors.primary)}
              ${this.renderRow('On User Balances', data.userBalanceStars, data.userBalanceSparks, NeonColors.gold)}
              ${this.renderSeparator()}
              ${this.renderRow('Actual System Total', data.totalSystemStars, data.totalSystemSparks, NeonColors.success)}
              ${this.renderRow('Delta (actual - expected)', deltaStars, data.conservationDelta, conservationColor)}
            </tbody>
          </table>
        </div>

        <div style="margin-top: 12px; font-size: 0.7rem; color: ${NeonColors.textDim};">
          Formula: Pool Free + In Game + User Balances = Total Deposited - Total Paid Out<br>
          Auto-refreshes every 5 seconds
        </div>
      </div>
    `

    this.container.querySelector('#pool-set-10k-btn')?.addEventListener('click', () => {
      void this.setPoolToExactSparks(10_000)
    })
  }

  private renderCard(label: string, stars: number, sparks: number, color: string, glow: string): string {
    return `
      <div style="
        ${neonPanel()}
        padding: 12px 16px; flex: 1; min-width: 150px; text-align: center;
      ">
        <div style="font-size: 0.7rem; color: ${NeonColors.textMuted}; margin-bottom: 6px;">${label}</div>
        <div style="font-size: 1.3rem; font-weight: 700; ${neonText(color, glow)}">${stars}${telegramStarIcon(14)}</div>
        <div style="font-size: 0.7rem; color: ${NeonColors.textDim}; margin-top: 2px;">${sparks} sparks</div>
      </div>
    `
  }

  private renderRow(label: string, stars: number, sparks: number, color: string): string {
    return `
      <tr style="border-bottom: 1px solid ${NeonColors.textDim};">
        <td style="padding: 6px 8px; font-size: 0.8rem; color: ${NeonColors.textMuted};">${label}</td>
        <td style="padding: 6px 8px; text-align: right; font-size: 0.85rem; color: ${color}; font-weight: 600;">${stars}${telegramStarIcon(14)}</td>
        <td style="padding: 6px 8px; text-align: right; font-size: 0.75rem; color: ${NeonColors.textDim};">${sparks} sp</td>
      </tr>
    `
  }

  private renderSeparator(): string {
    return `
      <tr>
        <td colspan="3" style="padding: 2px; border-bottom: 2px solid ${NeonColors.textDim};"></td>
      </tr>
    `
  }

  private async setPoolToExactSparks(targetSparks: number): Promise<void> {
    this.statusMessage = 'Updating DB pool...'
    this.statusIsError = false
    if (this.currentData) {
      this.renderBreakdown(this.currentData)
    }

    try {
      const res = await fetch('/api/admin/game-pool/set-sparks', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sparks: targetSparks,
          reason: 'admin test reset to exact fake-pool target',
        }),
      })
      const json = await res.json().catch(() => null)
      if (!res.ok || !json?.success) {
        throw new Error(json?.error ?? 'Failed to set pool')
      }

      this.statusMessage = `DB pool set to ${targetSparks} sparks`
      this.statusIsError = false
      await this.fetchBreakdown()
    } catch (err) {
      this.statusMessage = err instanceof Error ? err.message : 'Failed to set pool'
      this.statusIsError = true
      if (this.currentData) {
        this.renderBreakdown(this.currentData)
      }
    }
  }

  private escapeHtml(value: string): string {
    const div = document.createElement('div')
    div.textContent = value
    return div.innerHTML
  }
}
